import { FC } from "react";
import { Box, Checkbox, FormControlLabel, Stack, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useFormContext, Controller } from "react-hook-form";
import { TCompanyUserForm } from "@/features/company/types/CompanyUserForm";
import { useEnum } from "@/api/services/global/enum";
import { background, button, divider, text } from "@/theme/colors";
import { rounded, spacing } from "@/theme/variables";
import { PERMISSION_FIELDS } from "@/features/company/utiles/permission";

const DESCRIPTION =
  "所属機関のユーザーは、以下の情報および機能にアクセスできます。\nほとんどの画面は、登録支援機関様の画面と同じものが表示されるため、各データベースの入力内容にはお気をつけください。";

export const CompanyUserPermissions: FC = () => {
  const { control } = useFormContext<TCompanyUserForm>();
  const { data: permissionOptions } = useEnum("EUserPermission");

  const getLabel = (code: string) =>
    permissionOptions?.find((o) => o.value === code)?.label ?? code;

  return (
    <PermissionContainer>
      <Typography variant="head20Bold" color={text.primary}>
        アクセス権限
      </Typography>

      <Stack gap={2}>
        <Typography variant="cap12" color={text.primary} sx={{ whiteSpace: "pre-line" }}>
          {DESCRIPTION}
        </Typography>

        <Stack gap={1}>
          {PERMISSION_FIELDS.map((item) => (
            <Box key={item.field} sx={item.indented ? { pl: 4 } : undefined}>
              <Controller
                name={`permissions.${item.field}`}
                control={control}
                render={({ field }) => (
                  <StyledFormControlLabel
                    control={
                      <StyledCheckbox
                        checked={!!field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                        disabled={!item.active}
                      />
                    }
                    label={<Typography variant="body14" color={text.primary}>{getLabel(item.field)}</Typography>}
                  />
                )}
              />
            </Box>
          ))}
        </Stack>
      </Stack>
    </PermissionContainer>
  );
};

const PermissionContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  padding: ${spacing.md};
  gap: ${spacing.md};
  background-color: ${background.white};
  border: 1px solid ${divider.high};
  border-radius: ${rounded.sm};
`;

const StyledFormControlLabel = styled(FormControlLabel)`
  gap: ${spacing.xs};
  margin: 0;
  align-items: center;
  & .MuiFormControlLabel-label {
    padding: 0;
  }
`;

const StyledCheckbox = styled(Checkbox)`
  padding: 0;
  width: 24px;
  height: 24px;
  border-radius: ${rounded.xs};
  color: ${button.primary};
  &.Mui-checked {
    color: ${button.primary};
  }
  &.Mui-disabled {
    color: ${button.disable};
  }
  &.Mui-checked.Mui-disabled {
    color: ${button.disable};
  }
`;
